import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';
import { Eye, EyeOff } from "lucide-react";

interface FormData {
  email: string;
  password: string;
  role: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  role?: string;
}

const getInitialRole = () => {
  const hostname = window.location.hostname;
  if (hostname.includes("manager")) return "manager";
  if (hostname.includes("affiliate")) return "affiliate";
  return "admin";
};

const LoginPage = () => {
  const { roleParam } = useParams<{ roleParam?: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    role: getInitialRole(),
  });

  useEffect(() => {
    if (roleParam && ["admin", "manager", "affiliate"].includes(roleParam.toLowerCase())) {
      setFormData((prev) => ({
        ...prev,
        role: roleParam.toLowerCase(),
      }));
    }
  }, [roleParam]);

  useEffect(() => {
    const roleName = formData.role === 'admin' ? 'Admin' : formData.role.charAt(0).toUpperCase() + formData.role.slice(1);
    document.title = `${roleName} Portal`;
  }, [formData.role]);

  // Show toast if user was auto-logged out due to account being blocked
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'blocked') {
      toast.error('Your account has been blocked by the administrator. Please contact support.', {
        autoClose: 6000,
      });
      // Remove the query param from URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const isProductionDomain = window.location.hostname.includes("winsmash.com");

  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let valid = true;
    let tempErrors: FormErrors = {};

    if (!formData.email) {
      tempErrors.email = "Email is required";
      valid = false;
    }

    if (!formData.password) {
      tempErrors.password = "Password is required";
      valid = false;
    }

    setErrors(tempErrors);

    return valid;
  };



  const loginClick = async () => {
    if (!validateForm()) {
      toast.warn("Please fill all required fields");

      return;
    }

    try {
      const result = await apiClient.post(
        `/admin/login`,
        {
          email: formData.email,
          password: formData.password,
          role: formData.role,
        }
      );

      if (result.data.token) {
        localStorage.setItem(
          "token",
          result.data.token
        );
        
        if (result.data.refreshToken) {
          localStorage.setItem(
            "refreshToken",
            result.data.refreshToken
          );
        }

        if (result.data.role)
          localStorage.setItem(
            "role",
            result.data.role
          );

        if (result.data.uid)
          localStorage.setItem(
            "uid",
            result.data.uid
          );

        if (result.data.name)
          localStorage.setItem(
            "name",
            result.data.name
          );

        if (result.data.email)
          localStorage.setItem(
            "email",
            result.data.email
          );

        toast.success("Login Successful. Welcome back!");

        window.location.href = "/";
      }

    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Login failed");
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.warn("Please enter your email");
      return;
    }

    try {
      setForgotLoading(true);
      const res = await apiClient.post('/admin/forgotpasswrod', { email: forgotEmail });
      toast.success(res.data.message || "Password reset instructions sent.");
      setIsForgotMode(false);
      setForgotEmail("");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginClick();
  };

  return (
    <>
      <div className="min-h-screen bg-[#151929] flex items-center justify-center px-4 font-sans relative overflow-hidden">
        
        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none"></div>

        {/* Card */}
        <div className="w-full max-w-[420px] bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border border-gray-800 rounded-2xl p-8 shadow-2xl relative z-10">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="logo" className="h-10 object-contain drop-shadow-md" />
          </div>

          {isForgotMode ? (
            <>
              {/* Title */}
              <h1 className="text-center text-white text-xl font-bold tracking-wide mb-1 capitalize">
                Reset Password
              </h1>
              <p className="text-center text-gray-400 text-sm mb-8">
                Enter your email address to reset password
              </p>

              <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full bg-[#151929] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg py-3 text-sm font-bold shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:shadow-[0_0_25px_rgba(79,70,229,0.5)] transition-all duration-200 mt-2"
                >
                  {forgotLoading ? "Processing..." : "Reset Password"}
                </button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setIsForgotMode(false)}
                    className="text-indigo-400 hover:text-indigo-300 text-xs font-medium transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              {/* Title */}
              <h1 className="text-center text-white text-xl font-bold tracking-wide mb-1 capitalize">
                {formData.role === 'admin' ? 'Admin' : formData.role} Portal
              </h1>
              <p className="text-center text-gray-400 text-sm mb-8">
                Sign in to manage your {formData.role === 'admin' ? 'platform' : formData.role === 'manager' ? 'affiliates' : 'campaigns'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={`${formData.role === 'admin' ? 'admin' : formData.role}@winsmash.com`}
                className="w-full bg-[#151929] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1.5 font-medium">{errors.email}</p>}
            </div>

                {/* Password */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
                      Password
                    </label>
                    <button 
                      type="button" 
                      onClick={() => setIsForgotMode(true)}
                      className="text-indigo-400 hover:text-indigo-300 text-xs font-medium transition-colors"
                    >
                      Forgot?
                    </button>
                  </div>

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full bg-[#151929] border border-gray-700 rounded-lg pl-4 pr-12 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 p-1 transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-400 text-xs mt-1.5 font-medium">{errors.password}</p>}
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg py-3 text-sm font-bold shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:shadow-[0_0_25px_rgba(79,70,229,0.5)] transition-all duration-200 mt-2"
                >
                  Secure Login
                </button>

              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LoginPage;